import { Link } from "react-router-dom";

import type { Contact } from "@/app/providers/services";
import { Tooltip } from "@/components";

export type ContactIdentity = Pick<Contact, "identityId" | "identitySource">;

/** The Users admin page filtered by the contact's identity provider ID. */
export const getContactUserPath = (contact: ContactIdentity): string | undefined =>
  contact.identityId ? `/admin?identity_id=${encodeURIComponent(contact.identityId)}` : undefined;

/**
 * Whether the contact has an identity provider (sign-in) user: `identityId` is the user's ID, `null` when the
 * identity provider was checked and has none, and missing when it hasn't been checked. Only a linked contact
 * shows anything: a link to its user on the Users admin page.
 */
export const ContactIdentityStatus = ({
  contact,
}: {
  contact: ContactIdentity;
}) => {
  const userPath = getContactUserPath(contact);
  // Unchecked (undefined) and checked with no user (null) both show nothing.
  if (!userPath) return null;
  const description = `Identity provider user ${contact.identityId}${contact.identitySource ? ` (${contact.identitySource})` : ""}`;
  return (
    <Tooltip title={description}>
      <span data-testid="contact-identity-status">
        <Link
          to={userPath}
          className="text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          // Keep the contacts table's row click from opening the contact.
          onClick={(event) => event.stopPropagation()}
        >
          View user
        </Link>
      </span>
    </Tooltip>
  );
};
